import { useState } from 'react';
import type { ModuleConfig, LearnerState } from '@/lib/types';
import { useLearnerState } from '@/app/hooks/useLearnerState';

interface ModuleNavigationProps {
  config: ModuleConfig;
  state: LearnerState;
  isOpen: boolean;
  onToggle: () => void;
}

type Phase = 'content' | 'assessment' | 'reflection';

interface PhaseInfo {
  id: Phase;
  label: string;
}

const PHASES: PhaseInfo[] = [
  { id: 'content', label: 'Content' },
  { id: 'assessment', label: 'Assessment' },
  { id: 'reflection', label: 'Reflection' },
];

/* ── Inline SVG icon helpers ────────────────────────────────── */

function PhaseIcon({ phase, className = 'w-4 h-4' }: { phase: Phase; className?: string }) {
  switch (phase) {
    case 'content':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
        </svg>
      );
    case 'assessment':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
        </svg>
      );
    case 'reflection':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
        </svg>
      );
  }
}

function StatusDot({ status }: { status: string }) {
  switch (status) {
    case 'current':
      return (
        <span className="flex items-center justify-center w-4 h-4">
          <span className="w-2.5 h-2.5 rounded-full accent-bg animate-pulse" />
        </span>
      );
    case 'completed':
      return (
        <svg className="w-4 h-4" style={{ color: 'var(--color-success)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    default:
      return (
        <span className="flex items-center justify-center w-4 h-4">
          <span className="w-2 h-2 rounded-full bg-slate-300 dark:bg-slate-600" />
        </span>
      );
  }
}

function PartIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342" />
    </svg>
  );
}

/* ── Component ──────────────────────────────────────────────── */

export function ModuleNavigation({ config, state, isOpen, onToggle }: ModuleNavigationProps) {
  const [expandedParts, setExpandedParts] = useState<Set<number>>(new Set([0, 1, 2]));
  const { updatePosition } = useLearnerState();

  const togglePart = (partIndex: number) => {
    setExpandedParts((prev) => {
      const next = new Set(prev);
      if (next.has(partIndex)) {
        next.delete(partIndex);
      } else {
        next.add(partIndex);
      }
      return next;
    });
  };

  const jumpToPhase = (partIndex: number, phase: Phase) => {
    updatePosition({
      partIndex,
      phase,
      slideIndex: 0,
    });
  };

  const isCurrentLocation = (partIndex: number, phase: Phase) => {
    return (
      state.currentPosition.partIndex === partIndex &&
      state.currentPosition.phase === phase
    );
  };

  const getPhaseStatus = (partIndex: number, phase: Phase): 'current' | 'completed' | 'available' | 'locked' => {
    const partId = `part-${partIndex + 1}`;
    const partState = state.parts[partId];

    if (isCurrentLocation(partIndex, phase)) {
      return 'current';
    }

    if (partState) {
      if (phase === 'content' && partState.content?.completed) return 'completed';
      if (phase === 'assessment' && partState.assessment?.overallScore > 0) return 'completed';
      if (phase === 'reflection' && partState.reflection?.completed) return 'completed';
    }

    return 'available';
  };

  if (!isOpen) {
    return (
      <button
        onClick={onToggle}
        className="fixed left-0 top-20 z-40 p-2 bg-slate-800 dark:bg-slate-700 text-white rounded-r-lg hover:bg-slate-700 dark:hover:bg-slate-600 transition-colors"
        title="Open navigation"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    );
  }

  return (
    <div className="fixed left-0 top-0 bottom-0 w-80 bg-slate-50 dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700 z-40 overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 p-4 flex items-center justify-between">
        <h2 className="font-serif font-semibold text-slate-900 dark:text-slate-100">
          Navigation
        </h2>
        <button
          onClick={onToggle}
          className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-800 rounded transition-colors"
          title="Close navigation"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </div>

      {/* Module Title */}
      <div className="p-4 border-b border-slate-200 dark:border-slate-700">
        <div className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
          Module
        </div>
        <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
          {config.title}
        </div>
      </div>

      {/* Parts Tree */}
      <div className="p-2">
        {config.parts.map((_part, partIndex) => {
          const isExpanded = expandedParts.has(partIndex);
          const partId = `part-${partIndex + 1}`;

          return (
            <div key={partId} className="mb-1">
              {/* Part Header */}
              <button
                onClick={() => togglePart(partIndex)}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors group"
              >
                {/* Expand/Collapse Icon */}
                <svg
                  className={`w-4 h-4 text-slate-400 transition-transform ${
                    isExpanded ? 'rotate-90' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>

                {/* Part Icon */}
                <PartIcon className="w-5 h-5 text-slate-500 dark:text-slate-400" />

                {/* Part Title */}
                <span className="flex-1 text-sm font-medium text-slate-900 dark:text-slate-100">
                  Part {partIndex + 1}
                </span>

                {/* Part Completion */}
                {state.parts[partId]?.reflection?.completed && (
                  <svg className="w-4 h-4" style={{ color: 'var(--color-success)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </button>

              {/* Phases */}
              {isExpanded && (
                <div className="ml-6 mt-1 space-y-0.5">
                  {PHASES.map((phase) => {
                    const status = getPhaseStatus(partIndex, phase.id);
                    const isCurrent = isCurrentLocation(partIndex, phase.id);

                    return (
                      <button
                        key={phase.id}
                        onClick={() => jumpToPhase(partIndex, phase.id)}
                        className={`
                          w-full flex items-center gap-2.5 px-3 py-2 text-left rounded-lg transition-all
                          ${
                            isCurrent
                              ? 'accent-bg-medium accent-text-dark font-medium'
                              : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                          }
                        `}
                      >
                        {/* Status Dot */}
                        <StatusDot status={status} />

                        {/* Phase Icon */}
                        <PhaseIcon phase={phase.id} className={`w-4 h-4 ${isCurrent ? 'accent-text' : 'text-slate-400 dark:text-slate-500'}`} />

                        {/* Phase Label */}
                        <span className="flex-1 text-sm">
                          {phase.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer Info */}
      <div className="sticky bottom-0 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 p-4">
        <div className="text-xs text-slate-500 dark:text-slate-400">
          <div className="flex items-center gap-1.5">
            <span className="font-medium">Current:</span>
            <span>Part {state.currentPosition.partIndex + 1}</span>
            <span className="text-slate-300 dark:text-slate-600">/</span>
            <span>{PHASES.find(p => p.id === state.currentPosition.phase)?.label}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
